/**
 * Agent -> UI model adapters.
 *
 * The device agent's documents (`status.get`, `network.status`, `cloud.status`, `/ws` hub frames)
 * grew with the agent; the UI models in @glyph/protocol-ts (`DeviceStatus`, `NetworkStatus`,
 * `CloudLinkStatus`, the device topics) are what every screen and the MockClient use. The bench
 * showed the two had drifted apart (a blank Live screen on `network.eth.gateway`, a Cloud page
 * crash on `credentials.active_expires_at`, no live events because hub frames carry `topic`,
 * not an Envelope). Everything the agent sends is normalised here, once, with fixtures captured
 * from a real unit (`__fixtures__/agent-*.json`). Documents already in model shape (the mock,
 * the cloud gateway) pass through untouched.
 */
import type {
  AuthStatus,
  CaptionEvent,
  CloudLinkStatus,
  DeviceStatus,
  EngineStatus,
  HealthSnapshot,
  NetworkStatus,
  RecordStatus,
  SettingsChanged,
  Snapshot,
  SystemStats,
  Telemetry,
  TimecodeStatus,
  Topic,
  UpdateStatus,
  VersionInfo,
} from '@glyph/protocol-ts';
import type { DeviceId, Recording } from '@glyph/protocol-ts';
import type { ClientEvent } from '../client/types';

type Raw = Record<string, any>;
const obj = (x: unknown): Raw => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Raw) : {});
const num = (x: unknown, d = 0): number => (typeof x === 'number' && Number.isFinite(x) ? x : d);
const numOrNull = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
const str = (x: unknown, d = ''): string => (typeof x === 'string' ? x : d);
const strOrNull = (x: unknown): string | null => (typeof x === 'string' && x !== '' ? x : null);

const uplinkOf = (x: unknown): NetworkStatus['uplink'] =>
  x === 'eth' || x === 'wifi' || x === 'lte' ? x : 'none';
const linkState = (present: boolean, active: boolean): 'active' | 'standby' | 'down' =>
  !present ? 'down' : active ? 'active' : 'standby';

/** `network.status` (agent: ethernet / wifi_client / ap / lte / urls) -> NetworkStatus. */
export function normalizeNetwork(raw: unknown): NetworkStatus {
  const r = obj(raw);
  if (r.eth && r.wifi && r.ap && 'address' in r) return r as NetworkStatus; // already a model
  const eth = obj(r.ethernet);
  const wifi = obj(r.wifi_client);
  const ap = obj(r.ap);
  const lte = obj(r.lte);
  const uplink = uplinkOf(r.uplink);
  const urls: string[] = Array.isArray(r.urls) ? r.urls.map((u: Raw) => str(obj(u).url)).filter(Boolean) : [];
  const mdns = urls.find((u) => u.includes('.local'));
  const apBand = ap.band === '5' ? '5' : '2.4';
  return {
    uplink,
    address: strOrNull(r.primary_ip) ?? strOrNull(eth.ip) ?? strOrNull(wifi.ip),
    eth: {
      state: linkState(!!eth.link && !!eth.ip, uplink === 'eth'),
      address: strOrNull(eth.ip),
      gateway: strOrNull(eth.gateway),
      dns: Array.isArray(eth.dns) ? eth.dns.map(String) : [],
      speed: strOrNull(eth.speed),
    },
    wifi: {
      state: linkState(!!wifi.connected, uplink === 'wifi'),
      ssid: strOrNull(wifi.ssid),
      rssi_dbm: numOrNull(wifi.signal),
      address: strOrNull(wifi.ip),
      saved: Array.isArray(wifi.saved) ? wifi.saved.map(String) : [],
    },
    lte: {
      state: linkState(!!lte.enabled && !!obj(lte.modem).present, uplink === 'lte'),
      apn: strOrNull(lte.apn),
    },
    ap: {
      enabled: !!ap.enabled,
      ssid: str(ap.ssid),
      ip: str(ap.ip),
      band: apBand,
      clients: (Array.isArray(ap.clients) ? ap.clients : []).map((c: Raw) => ({
        host: str(c.host ?? c.hostname ?? c.name),
        mac: str(c.mac),
        ip: strOrNull(c.ip),
      })),
      urls,
    },
    mdns_host: mdns
      ? mdns
          .replace(/^https?:\/\//, '')
          .split('/')[0]!
          .split(':')[0]!
      : null,
  };
}

/** `cloud.status` (+ `status.get.credentials`) -> CloudLinkStatus. */
export function normalizeCloud(raw: unknown, credentials?: unknown): CloudLinkStatus {
  const r = obj(raw);
  if (r.identity && obj(r.identity).algorithm && r.credentials) return r as CloudLinkStatus;
  const id = obj(r.identity);
  const cred = obj(credentials ?? r.credentials);
  const asg = r.assignment ? obj(r.assignment) : null;
  return {
    enabled: !!r.enabled,
    enrolled: !!r.configured || !!id.enrolled_at,
    connected: !!r.connected,
    url: str(r.url),
    org: asg ? strOrNull(asg.org_name) ?? strOrNull(obj(asg.customer_org).name) : strOrNull(r.org),
    device_id: strOrNull(r.device_id),
    heartbeat_s: num(r.heartbeat_s, 5),
    rtt_ms: numOrNull(r.rtt_ms),
    reconnects_24h: num(r.reconnects_24h),
    last_connected_at: numOrNull(r.last_connected_at),
    identity: { algorithm: 'Ed25519', key_id: str(id.key_id), enrolled_at: numOrNull(id.enrolled_at) },
    credentials: {
      active_expires_at: numOrNull(cred.expires_at ?? cred.active_expires_at),
      standby_expires_at: numOrNull(cred.standby_expires_at),
    },
    // the agent stores the gateway's WelcomeAssignment: {org_id, event_id, customer_org: {name}, event: {name}}
    assignment: asg
      ? {
          org_name: str(asg.org_name) || str(obj(asg.customer_org).name),
          event_id: strOrNull(asg.event_id) ?? strOrNull(obj(asg.event).id),
          event_name: strOrNull(asg.event_name) ?? strOrNull(obj(asg.event).name),
        }
      : null,
    fleet_status: str(r.fleet_status),
    auto_enrol: {
      available: !!obj(r.auto_enrol).available,
      url: str(obj(r.auto_enrol).url),
      attempts: num(obj(r.auto_enrol).attempts),
      last_error: str(obj(r.auto_enrol).last_error),
      done: !!obj(r.auto_enrol).done,
    },
  };
}

/** `status.get.version` (UpdateManager.version_info) -> VersionInfo. */
export function normalizeVersion(raw: unknown): VersionInfo {
  const r = obj(raw);
  if (typeof r.agent === 'string' && 'slot' in r && !('version' in r)) return r as VersionInfo;
  return {
    agent: str(r.agent ?? r.version),
    os: str(r.os),
    previous: strOrNull(r.previous),
    staged: strOrNull(r.staged),
    slot: r.slot === 'B' ? 'B' : 'A',
  };
}

/** `status.get.stats` -> SystemStats. */
export function normalizeStats(raw: unknown): SystemStats {
  const r = obj(raw);
  return {
    cpu_pct: num(r.cpu_pct),
    temp_c: numOrNull(r.temp_c ?? r.cpu_temp),
    load_1m: num(r.load_1m),
    mem_pct: num(r.mem_pct),
    disk_free_gb: num(r.data_free_gb ?? r.disk_free_gb),
    disk_total_gb: num(r.data_total_gb ?? r.disk_total_gb),
    net_up_bps: num(r.net_up_bps, num(r.net_tx_kbps) * 1000),
    net_down_bps: num(r.net_down_bps, num(r.net_rx_kbps) * 1000),
    audio_dbfs: Array.isArray(r.audio_dbfs) ? r.audio_dbfs.map((x: unknown) => num(x, -120)) : [],
    under_voltage: !!r.under_voltage,
    throttled: !!r.throttled,
    uptime_s: num(r.uptime_s),
  };
}

const engineState = (running: boolean, asr: Raw, health?: Raw): EngineStatus['state'] => {
  const s = str(asr.state ?? health?.state);
  if (!running) return s === 'error' ? 'error' : 'stopped';
  if (s === 'reconnecting' || s === 'starting' || s === 'error') return s;
  return 'running';
};

/** `status.get.engine` (+ captions stats, health.deepgram) -> EngineStatus. */
export function normalizeEngine(
  raw: unknown,
  captions?: unknown,
  health?: unknown,
  settings?: unknown,
): EngineStatus {
  const r = obj(raw);
  if (typeof r.state === 'string' && 'overlay' in r && 'words_per_min' in r) return r as EngineStatus;
  const asr = obj(r.asr);
  const dg = obj(obj(health).deepgram);
  return {
    running: !!r.running,
    state: engineState(!!r.running, asr, dg),
    model: str(r.model ?? asr.model ?? obj(obj(settings).deepgram).model),
    connected: !!(asr.connected ?? dg.state === 'ok'),
    rtt_ms: numOrNull(asr.rtt_ms ?? r.rtt_ms),
    words_per_min: num(obj(captions).words_per_min),
    overlay: (['none', 'blank', 'test_pattern', 'badge'] as const).includes(r.overlay) ? r.overlay : 'none',
    locked: !!r.locked,
  };
}

export function normalizeRecord(raw: unknown): RecordStatus {
  const r = obj(raw);
  return {
    active: !!r.active,
    name: strOrNull(r.name ?? r.session ?? r.session_name),
    elapsed_s: num(r.elapsed_s),
    events: num(r.events ?? r.event_count),
    drive: str(r.drive ?? r.drive_label),
    free_gb: numOrNull(r.free_gb),
  };
}

export function normalizeTimecode(raw: unknown): TimecodeStatus {
  const r = obj(raw);
  return {
    tc: str(r.tc, '--:--:--:--'),
    source: str(r.source, 'off'),
    framerate: num(r.framerate, 30),
    locked: r.locked === true || (r.stale === false && !!r.tc),
    drift_ppm: num(r.drift_ppm),
  };
}

/** Component health as the UI reads it (`health.audio.state` ...); extra agent fields pass through. */
export function normalizeHealth(raw: unknown): HealthSnapshot {
  const r = obj(raw);
  const comp = (k: string) => ({
    state: str(obj(r[k]).state, 'unknown'),
    message: str(obj(r[k]).message),
    last_activity_ts: numOrNull(obj(r[k]).last_activity_ts),
    error_count: num(obj(r[k]).error_count),
  });
  return {
    ...r,
    audio: comp('audio'),
    deepgram: comp('deepgram'),
    engine: comp('engine'),
    network: comp('network'),
    renderer: comp('renderer'),
    cloud: comp('cloud'),
    ts: num(r.ts, Date.now() / 1000),
  } as HealthSnapshot;
}

/** `status.get` -> DeviceStatus. */
export function normalizeStatus(raw: unknown): DeviceStatus {
  const r = obj(raw);
  const network = normalizeNetwork(r.network);
  return {
    ...r,
    ok: r.ok !== false,
    ts: num(r.ts, Date.now() / 1000),
    health: normalizeHealth(r.health),
    engine: normalizeEngine(r.engine, r.captions, r.health),
    stats: normalizeStats(r.stats),
    network,
    record: normalizeRecord(r.record),
    timecode: normalizeTimecode(r.timecode),
    version: normalizeVersion(r.version),
    system_state: str(r.system_state, 'idle'),
    setup_completed: !!r.setup_completed,
    auth_required: !!r.auth_required,
    cloud: normalizeCloud(r.cloud, r.credentials),
    partner: {
      enabled: !!obj(r.partner).enabled,
      role: obj(r.partner).role === 'backup' ? 'backup' : 'primary',
      follow: (['off', 'on', 'auto'] as const).includes(obj(r.partner).follow)
        ? obj(r.partner).follow
        : 'off',
      peer: strOrNull(obj(r.partner).peer ?? obj(r.partner).peer_host),
      following: !!obj(r.partner).following,
    },
    outputs: Array.isArray(r.outputs) ? r.outputs : [],
    update: r.update
      ? ({ ...obj(r.update), locked: !!(obj(r.update).locked ?? obj(r.version).locked) } as UpdateStatus & {
          locked: boolean;
        })
      : undefined,
  } as unknown as DeviceStatus;
}

/** Telemetry topic payload from a `status` tick / status.get document. */
export function telemetryFromStatus(raw: unknown): Telemetry {
  const r = obj(raw);
  const st = normalizeStats(r.stats);
  const h = obj(r.health);
  return {
    cpu_pct: st.cpu_pct,
    temp_c: st.temp_c,
    mem_pct: st.mem_pct,
    disk_free_gb: num(obj(r.stats).disk_free_gb),
    data_free_gb: numOrNull(obj(r.stats).data_free_gb),
    net_up_bps: st.net_up_bps,
    net_down_bps: st.net_down_bps,
    uplink: uplinkOf(obj(r.network).uplink),
    audio_dbfs: st.audio_dbfs,
    engine_running: !!obj(r.engine).running,
    deepgram_state: str(obj(h.deepgram).state, 'stopped'),
    words_per_min: num(obj(r.captions).words_per_min),
    dg_audio_s_total: num(obj(r.engine).dg_audio_s_total),
    recording: !!obj(r.record).active,
    under_voltage: st.under_voltage,
    throttled: st.throttled,
    uptime_s: st.uptime_s,
    agent_version: str(obj(r.version).version ?? obj(r.version).agent ?? h.version),
    update_locked: !!(obj(r.update).locked ?? obj(r.version).locked),
    dg_latency_ms: numOrNull(obj(obj(r.engine).asr).rtt_ms),
  };
}

/** Snapshot topic payload from a `status` tick. */
export function snapshotFromStatus(raw: unknown): Snapshot {
  const r = obj(raw);
  return {
    agent_version: str(obj(r.version).version ?? obj(r.version).agent),
    os_version: str(obj(r.version).os),
    settings: {},
    health: normalizeHealth(r.health),
    telemetry: telemetryFromStatus(r),
    outputs: Array.isArray(r.outputs) ? r.outputs : [],
    commands: [],
  } as unknown as Snapshot;
}

/** `auth.status` (agent: required / authed / locked) -> AuthStatus (pin_required / unlocked). */
/**
 * `record.sessions` row (agent recorder: name, size, has_audio, files, display_name, tags, protected,
 * drive, start_ts, end_ts, events, tc_source, framerate, open) -> `Recording`. The agent does not count
 * words or carry cloud fields, and the Recordings page formats `words` on every row, so every field the
 * page touches gets a value here (a 1 s test session without them blanked the page: bench 2026-10-19).
 */
export function normalizeLocalRecording(raw: unknown): Recording {
  const r = obj(raw);
  const name = str(r.name);
  return {
    ...(r as object),
    id: str(r.id, name),
    device_id: str(r.device_id, 'self') as DeviceId,
    name,
    display_name: strOrNull(r.display_name),
    start_ts: num(r.start_ts),
    end_ts: numOrNull(r.end_ts),
    size: num(r.size),
    events: num(r.events),
    sha256: strOrNull(r.sha256),
    drive: str(r.drive, 'internal') as Recording['drive'],
    has_audio: !!r.has_audio,
    tags: Array.isArray(r.tags) ? r.tags.map(String) : [],
    protected: !!r.protected,
    event_name: strOrNull(r.event_name),
    folder: str(r.folder),
    words: num(r.words),
    tc_start: str(r.tc_start),
    framerate: num(r.framerate),
    upload_status: (['local_only', 'uploading', 'synced', 'deleted_on_device'] as const).includes(r.upload_status) ? r.upload_status : 'local_only',
  } as Recording;
}

export function normalizeAuthStatus(raw: unknown): AuthStatus {
  const r = obj(raw);
  if (typeof r.pin_required === 'boolean') return r as AuthStatus;
  const acc = obj(r.accounts);
  return {
    pin_required: !!r.required,
    unlocked: !!r.authed,
    attempts_left: numOrNull(r.attempts_left),
    role: r.role === 'tech' ? 'tech' : r.role === 'admin' ? 'admin' : null,
    accounts: {
      admin: !!acc.admin,
      tech: !!acc.tech,
      admin_username: str(acc.admin_username, 'fdgadmin'),
      tech_username: str(acc.tech_username, 'tech'),
    },
  };
}

// ── /ws hub frames ───────────────────────────────────────────────────────────────────────────

/** UI topic -> agent hub topic (`WS_TOPICS` in device/agent/glyph_agent/api/app.py). */
export const HUB_TOPIC: Partial<Record<Topic, string>> = {
  snapshot: 'status',
  telemetry: 'status',
  health: 'status',
  settings_changed: 'status',
  update_status: 'update',
  caption_event: 'captions',
  alert: 'alerts',
  levels: 'levels',
  timecode: 'timecode',
  logs: 'logs',
  preview: 'preview',
  audio_opus: 'audio',
};

export function hubTopicsFor(topics: readonly Topic[]): string[] {
  return [...new Set(topics.map((t) => HUB_TOPIC[t]).filter((t): t is string => !!t))];
}

const captionEvent = (kind: CaptionEvent['kind'], f: Raw): CaptionEvent => ({
  kind,
  id: num(f.id),
  text: str(f.text),
  words: Array.isArray(f.words) ? f.words : [],
  ts: num(f.ts, Date.now() / 1000),
  speaker: numOrNull(f.speaker),
  as0: numOrNull(f.as0),
  as1: numOrNull(f.as1),
});

/**
 * One agent hub frame (`{topic, type?, ...}`) -> zero or more UI topic events. A `status` tick
 * fans out to telemetry + snapshot, a captions snapshot to one event per final.
 */
export function hubFrameToEvents(msg: unknown, deviceId: DeviceId = 'self'): ClientEvent[] {
  const m = obj(msg);
  const topic = str(m.topic ?? m.t);
  if (!topic || topic === 'ping' || topic === 'pong') return [];
  const ts = typeof m.ts === 'number' ? m.ts * (m.ts < 1e12 ? 1000 : 1) : Date.now();
  const ev = (t: Topic, data: unknown): ClientEvent => ({ topic: t, deviceId, ts, data }) as ClientEvent;
  // Already an Envelope {t, d} (cloud gateway style): pass through.
  if (typeof m.t === 'string' && m.d !== undefined && m.topic === undefined) return [ev(m.t as Topic, m.d)];
  switch (topic) {
    case 'status': {
      const type = str(m.type);
      if (type === 'health') return [ev('health', normalizeHealth(m))];
      if (type === 'tick')
        return [
          ev('health', normalizeHealth(m.health)),
          ev('telemetry', telemetryFromStatus(m)),
          ev('snapshot', snapshotFromStatus(m)),
        ];
      if (type === 'settings_changed') {
        const d: SettingsChanged = {
          sections: Array.isArray(m.sections) ? m.sections.map(String) : [],
          settings: {},
          source: (['local', 'cloud', 'partner', 'system'] as const).includes(m.source) ? m.source : 'system',
          actor: strOrNull(m.actor),
          ts: ts / 1000,
        };
        return [ev('settings_changed', d)];
      }
      if (type === 'outputs') return [ev('snapshot', { outputs: Array.isArray(m.outputs) ? m.outputs : [] })];
      return [];
    }
    case 'captions': {
      const type = str(m.type);
      if (type === 'partial' || type === 'final' || type === 'clear')
        return [ev('caption_event', captionEvent(type, m))];
      if (type === 'snapshot') {
        const out: ClientEvent[] = [ev('caption_event', captionEvent('clear', { id: 0, text: '' }))];
        for (const f of Array.isArray(m.finals) ? m.finals : [])
          out.push(ev('caption_event', captionEvent('final', obj(f))));
        if (m.partial) out.push(ev('caption_event', captionEvent('partial', obj(m.partial))));
        return out;
      }
      return [];
    }
    case 'levels':
      return [ev('levels', { dbfs: Array.isArray(m.dbfs) ? m.dbfs : [] })];
    case 'update':
      return [ev('update_status', m)];
    case 'alerts':
      return [ev('alert', m)];
    case 'timecode':
      return [ev('timecode', normalizeTimecode(m))];
    case 'logs':
      return [ev('logs', m)];
    case 'preview':
      return [ev('preview', m)];
    default:
      return [];
  }
}
